import { apiFetch } from './config';

export type AbortLine = string | { condition?: string; action?: string; report?: string; [key: string]: unknown };

// WebAuthn request options encode binary fields as base64url strings over JSON.
export interface FidoRequestOptions extends Omit<PublicKeyCredentialRequestOptions, 'challenge' | 'allowCredentials'> {
  challenge: string;
  allowCredentials?: Array<Omit<PublicKeyCredentialDescriptor, 'id'> & { id: string }>;
}

export interface Dispatch {
  dispatch_id: string;
  dispatch_key: string;
  document_id: string | null;
  content_sha256: string | null;
  status: 'draft' | 'pending' | 'claimed' | 'in_progress' | 'complete' | 'blocked' | 'superseded' | 'rejected' | 'withdrawn';
  assigned_seat: string;
  claimed_by: string | null;
  claimed_at: string | null;
  completed_at: string | null;
  blocked_on: string[] | null;
  supersedes: string | null;
  completion_ref: string | null;
  company_id: string;
  created_at: string;
  updated_at: string;
  document_name?: string;
  step_count?: number;
  steps_complete?: number;
}

export interface BadgeResponse {
  human_active: number;
  open_escalations: number;
  badge_total: number;
}

export interface Escalation {
  execution_id: string;
  step_id: string;
  dispatch_run_id: string;
  status: string;
  report: Record<string, unknown> | null;
  grade: 'pass' | 'fail' | 'ambiguous' | null;
  grade_reasoning: string | null;
  grade_evidence: Record<string, unknown> | null;
  graded_by: string | null;
  graded_at: string | null;
  claimed_by: string | null;
  claimed_seat: string | null;
  released_at: string | null;
  reported_at: string | null;
  human_verdict: 'proceed' | 'reject' | 'amend' | null;
  verdict_reason: string | null;
  verdict_by: string | null;
  verdict_at: string | null;
  step_name: string;
  sequence: number;
  config: {
    instruction?: string;
    abort_lines?: AbortLine[];
    [key: string]: unknown;
  };
  consequence_tier: string;
  escalation_owner: string | null;
  grader_mode: string;
  report_schema: Record<string, unknown> | null;
  release_condition: Record<string, unknown> | null;
  checker_profile: string | null;
  dispatch_key: string;
  dispatch_id: string;
  assigned_seat: string;
  dispatch_status: string;
}

export interface StalledDispatch {
  dispatch_id: string;
  dispatch_key: string;
  status: string;
  assigned_seat: string;
  claimed_by: string | null;
  claimed_at: string | null;
  created_at: string;
  updated_at: string;
  document_name?: string;
  last_step_activity: string;
  age_days: number;
}

export interface BlockedOnWitness {
  execution_id: string;
  step_id: string;
  dispatch_run_id: string;
  step_status: string;
  report: Record<string, unknown> | null;
  grade: string | null;
  grade_reasoning: string | null;
  grade_evidence: Record<string, unknown> | null;
  graded_at: string | null;
  graded_by: string | null;
  claimed_by: string | null;
  claimed_seat: string | null;
  reported_at: string | null;
  step_name: string;
  sequence: number;
  config: {
    instruction?: string;
    abort_lines?: AbortLine[];
    [key: string]: unknown;
  };
  consequence_tier: string;
  escalation_owner: string | null;
  grader_mode: string;
  report_schema: Record<string, unknown> | null;
  release_condition: Record<string, unknown> | null;
  checker_profile: string | null;
  dispatch_key: string;
  dispatch_id: string;
  assigned_seat: string;
  dispatch_status: string;
  hours_waiting: number;
}

export interface DispatchStep {
  step_id: string;
  sequence: number;
  name: string;
  consequence_tier: string;
  grader_mode: string;
  escalation_owner: string | null;
  config: {
    instruction?: string;
    abort_lines?: AbortLine[];
    [key: string]: unknown;
  } | null;
  report_schema: Record<string, unknown> | null;
  release_condition: Record<string, unknown> | null;
  checker_profile: string | null;
  execution_id: string | null;
  execution_status: string | null;
  report: Record<string, unknown> | null;
  grade: string | null;
  grade_reasoning: string | null;
  grade_evidence: Record<string, unknown> | null;
  step_claimed_by: string | null;
  claimed_seat: string | null;
  human_verdict: string | null;
  verdict_reason: string | null;
  reported_at: string | null;
  graded_at: string | null;
  released_at: string | null;
}

export interface DispatchStepsResponse {
  dispatch_id: string;
  dispatch_key: string;
  dispatch_status: string;
  assigned_seat: string;
  document_name: string | null;
  document_abstract: string | null;
  steps: DispatchStep[];
  count: number;
}

export interface SeatMandate {
  mandate_id: string;
  mandate_key: string;
  display_name: string | null;
  capability: string;
  tier_ceiling: string;
  allowed_checker_profiles: string[];
  active: boolean;
  created_at: string;
}

interface StepsResponse {
  dispatch_id: string;
  dispatch_key: string;
  dispatch_status: string;
  assigned_seat: string;
  document_name: string | null;
  document_abstract: string | null;
  steps: DispatchStep[];
  count: number;
}

export interface ListResponse {
  records: Dispatch[];
  seats: string[];
  summary: { assigned_active: number; unrouted_active: number };
  pagination: { page: number; limit: number; total: number; total_pages: number };
}

interface StalledResponse {
  stalled: StalledDispatch[];
  threshold_days: number;
  count: number;
}

interface BlockedOnWitnessResponse {
  blocked: BlockedOnWitness[];
  count: number;
}

interface MandatesResponse {
  mandates: SeatMandate[];
  count: number;
}

interface EscalationsResponse {
  escalations: Escalation[];
  count: number;
}

interface ResolveResponse {
  execution_id: string;
  human_verdict: string;
  new_status: string;
  verdict_by: string;
  action: string;
}

function unwrap<T>(response: { success: boolean; data?: any; error?: string }): { success: boolean; data?: T; error?: string } {
  if (response.success && response.data) {
    const inner = response.data?.data !== undefined ? response.data.data : response.data;
    return { success: true, data: inner as T };
  }
  return { success: false, error: response.error };
}

export const dispatchesApi = {
  list: async (params?: Record<string, string>) =>
    unwrap<ListResponse>(await apiFetch<any>(`/api/dispatches?${new URLSearchParams(params || {}).toString()}`)),

  pendingCount: async () =>
    unwrap<ListResponse>(await apiFetch<any>('/api/dispatches?status=pending&limit=1')),

  badgeCount: async () =>
    unwrap<BadgeResponse>(await apiFetch<any>('/api/dispatches?view=badge')),

  action: async (body: Record<string, unknown>) =>
    unwrap<{ dispatch: Dispatch; action: string }>(await apiFetch<any>('/api/dispatches/action', {
      method: 'POST',
      body: JSON.stringify(body),
    })),

  stalledDispatches: async (thresholdDays?: number) =>
    unwrap<StalledResponse>(await apiFetch<any>(`/api/dispatches?view=stalled${thresholdDays ? `&threshold_days=${thresholdDays}` : ''}`)),

  blockedOnWitness: async () =>
    unwrap<BlockedOnWitnessResponse>(await apiFetch<any>('/api/dispatches?view=blocked_on_witness')),

  mandates: async () =>
    unwrap<MandatesResponse>(await apiFetch<any>('/api/dispatches?view=mandates')),

  steps: async (dispatchId: string) =>
    unwrap<StepsResponse>(await apiFetch<any>(`/api/dispatches?view=steps&dispatch_id=${encodeURIComponent(dispatchId)}`)),

  escalations: async (params?: { dispatch_id?: string; include_resolved?: string }) =>
    unwrap<EscalationsResponse>(await apiFetch<any>(`/api/dispatches/escalations?${new URLSearchParams(params || {}).toString()}`)),

  resolveEscalation: async (body: { execution_id: string; human_verdict: string; verdict_reason: string; fido_assertion?: Record<string, unknown> }) =>
    unwrap<ResolveResponse>(await apiFetch<any>('/api/dispatches/escalations/resolve', {
      method: 'POST',
      body: JSON.stringify(body),
    })),

  fidoConfig: async () =>
    unwrap<{ config: Record<string, string> }>(await apiFetch<any>('/api/fido/config')),

  fidoAssertOptions: async (body: { execution_context?: Record<string, unknown> }) =>
    unwrap<{ options: FidoRequestOptions; bound_context: Record<string, unknown> | null }>(
      await apiFetch<any>('/api/fido/assert/options', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    ),
};
